/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";

// @mui icons
// import Icon from "@mui/material/Icon";
import { Icon } from '@iconify/react';

// import RTL from "layouts/rtl";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import ResetPassword from "layouts/authentication/reset-password/cover/resetPassword";
import AddOwner from "pages/addOwner";
import AddScreen from "pages/addScreen";
import ManageOwner from "pages/manageOwner";
import EditScreen from "pages/editScreen";
import Facilities from "pages/facilities";
import Languages from "pages/languages";
import Theatres from "pages/theatres";
import AddTheatre from "pages/addTheatre";
import EditTheatre from "pages/editTheatre";
import SingleTheatre from "pages/singleTheatre";
import SingleScreen from "pages/singleScreen";
import AddZone from "pages/addZone";
import SingleZone from "pages/singleZone";
import AddShowTime from "pages/addShowTime";
import EditShowTime from "pages/editShowTime";
import AddShows from "pages/addShows";
import Movies from "pages/movies";
import AddMovies from "pages/addMovies";
import AddLanguages from "pages/addLanguages";
import EditFacilities from "pages/editFacilities";
import Genre from "pages/genre";
import AddGenre from "pages/addGenre";
import EditGenre from "pages/editGenre";
import AddSoundSystem from "pages/addSoundSystemType";
import SoundSystem from "pages/soundSystemType";
import EditSoundSystem from "pages/editSoundSystemType";
import ProjectionType from "pages/projectionType";
import AddProjectionType from "pages/addProjectionType";
import EditLanguage from "pages/editLanguages";
import EditProjectionType from "pages/editProjectionType";
import CastList from "pages/castList";
import AddCastList from "pages/addCastList";
import EditCastList from "pages/editCastList";
import AddCrewList from "pages/addCrewList";
import CrewList from "pages/crewList";
import EditCrewList from "pages/editCrewList";
import EditMovies from "pages/editMovies";
import ViewMovie from "pages/viewMovie";
import CensorTypes from "pages/censorTypes";
import AddCensorType from "pages/addCensorType";
import EditCensorType from "pages/editCensorType";
import AddFacilities from "pages/addFacilities";
import SuperAdmin from "pages/superAdmin";
import TheatreOwners from "pages/theatreOwners";
import AddTheatreOwner from "pages/addTheatreOwner";
import EditTheatreOwner from "pages/editTheatreOwner";
import TheatreOwner from "pages/theatreOwner";
import Bookings from "pages/bookings";
import BookSeats from "components/TicketBooking/bookSeats";
import GetTickets from "components/TicketBooking/getTickets";
import ViewTickets from "pages/viewTickets";
import ViewEventTickets from "pages/viewEventTickets";
import Events from "pages/events";
import AddEvent from "pages/addEvent";
import EditEvent from "pages/editEvent";
import SingleEvent from "pages/singleEvent";
import ViewForm from "pages/viewForm";
import RegisterEvent from "pages/registerEvent";
import ViewEventRegistrations from "pages/viewEventRegistrations";
import AddMainEvent from "pages/addMainEvent";
import EditMainEvent from "pages/editMainEvent";
import EditZone from "pages/editZone";
import BookingsAdmin from "pages/bookingsAdmin";
import Promotions from "pages/promotions";
import FeedBacks from "pages/feedBack";
import HelpAndSupport from "pages/helpAndSupport";
import Reports from "pages/reports";
import Settings from "pages/settings";
import EventsAdmin from "pages/eventsAdmin";
import EventOrganizers from "pages/eventOrganizers";
import AddEventOrganizer from "pages/addEventOrganizer";
import EventOrganizer from "pages/eventOrganizer";
import EditEventOrganizer from "pages/editEventOrganizer";
import EventOrganizations from "pages/eventOrganizations";
import AddEventOrganization from "pages/addEventOrganization";
import SingleEventOrganization from "pages/singleEventOrganization";
import EditEventOrganization from "pages/editEventOrganization";
import Venues from "pages/venues";
import AddVenue from "pages/addVenue";
import SingleVenue from "pages/singleVenue";
import EditVenue from "pages/editVenue";
import EventBookings from "pages/eventBookings";
import AddEventZone from "pages/addEventZone";
import SingleEventZone from "pages/singleEventZone";
import SeatBookings from "components/EventBooking/seatBookings";
import GetEventTicket from "components/EventBooking/getEventTicket";
import SingleTicketView from "components/TicketBooking/singleTicketView";
import TicketBookingAndRegistration from "components/EventBooking/ticketBookingAndRegistration";
import BookedTicketView from "components/EventBooking/bookedTicketView";
import ViewPermissions from "pages/viewPermissions";
import AddPermissions from "pages/addPermissions";
import EditPermissions from "pages/editPermissions";
import ViewDefaultRoles from "pages/viewDefaultRoles";
import AddDefaultRoles from "pages/addDefaultRoles";
import EditDefaultRoles from "pages/editDefaultRoles";
import ViewUserRoles from "pages/viewUserRoles";
import AddUserRoles from "pages/addUserRoles";
import UserRoleInvite from "pages/userRoleInvite";
import EditUserRoles from "pages/editUserRoles";
import SingleUserRole from "pages/singleUserRole";
import SingleDefaultRole from "pages/singleDefaultRole";
import AddEventWrapper from "pages/addEventWrapper";
import EventCategory from "pages/eventCategory";
import EditEventZone from "pages/editEventZone";
import AllUsers from "pages/customers";
import AddUser from "pages/addUser";
import VerifyEmail from "pages/verifyEmail";
import ViewPermissionTypes from "pages/viewPermissionTypes";
import VerifyToken from "pages/verifyToken";
import ViewTicketsByEvents from "pages/viewTicketsByEvents";
import DetailedTicketPageByEvents from "pages/detailedTicketPageByEvents";
import DetailedTicketPageByRegistration from "pages/detailedTicketPageByRegistration";
import VenueFeature from "pages/venueFeature";
import VenueCities from "pages/venueCities";
import AddVenueCity from "pages/addVenueCity";
import EditVenueCity from "pages/editVenueCity";
import SingleVenueCity from "pages/singleVenueCity";
import EditRegistration from "components/FormSubmission/editRegistration";

const routes = [
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon icon="fluent-mdl2:signin" />,
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon icon="material-symbols:account-circle" />,
    route: "/authentication/sign-up",
    component: <SignUp />,
  },
  {
    // type: "collapse",
    // name: "Reset Password",
    // key: "reset-password",
    // icon: <Icon icon="material-symbols:account-circle" />,
    route: "/authentication/reset-password",
    component: <ResetPassword />,
  },
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon icon="mage:home-2" />,
    route: "/dashboard",
    component: <Dashboard />,
    permission: "default",
  },
  {
    type: "title",
    name: "Events Management",
    permission: "events_title",
  },
  {
    type: "collapse",
    name: "Organizations",
    key: "eventOrganizations",
    icon: <Icon icon="fluent:organization-20-regular" />,
    component: <EventOrganizations />,
    // permission: "organizations_management",
    collapse: [
      {
        type: "collapse",
        name: "View Organizations",
        key: "viewOrganizations",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/eventOrganizations",
        component: <EventOrganizations />,
        permission: "view_organizations",
      },
      {
        type: "collapse",
        name: "Add Organization",
        key: "addOrganization",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/add-eventOrganization",
        component: <AddEventOrganization />,
        permission: "create_organizations",
      },
    ],
  },
  {
    type: "collapse",
    name: "Organization",
    key: "eventOrganization",
    icon: <Icon icon="fluent:organization-horizontal-20-regular" />,
    route: "/eventOrganization",
    component: <SingleEventOrganization />,
    permission: "view_singleOrganization",
  },
  // {
  //   type: "collapse",
  //   name: "Admin Events",
  //   key: "eventsAdmin",
  //   icon: <Icon icon="uit:comment-dots" />,
  //   route: "/eventsAdmin",
  //   component: <EventsAdmin />,
  // },
  // {
  //   type: "collapse",
  //   name: "Organizer Events",
  //   key: "events",
  //   icon: <Icon icon="fluent-emoji-high-contrast:party-popper" />,
  //   route: "/events",
  //   component: <Events />,
  // },

  // removed as it duplicates under users-event organizer
  // {
  //   type: "collapse",
  //   name: "Event Organizers",
  //   key: "manageOrganizers",
  //   icon: <Icon icon="ph:users-four-thin" />,
  //   permission: "read",
  //   collapse: [
  //     {
  //       type: "collapse",
  //       name: "Add Event Organizer",
  //       key: "addEventOrganizer",
  //       icon: <Icon icon="icon-park-outline:dot" />,
  //       route: "/allUsers/add-eventOrganizer",
  //       component: <AddEventOrganizer />,
  //       permission: "create",
  //     },
  //     {
  //       type: "collapse",
  //       name: "List Event Organizers",
  //       key: "listEventOrganizers",
  //       icon: <Icon icon="icon-park-outline:dot" />,
  //       route: "/allUsers/eventOrganizers",
  //       component: <EventOrganizers />,
  //       permission: "read",
  //     },
  //   ]
  // },
  {
    route: "/allUsers/eventOrganizer/:id",
    component: <EventOrganizer />,
    permission: "read",
  },
  {
    route: "/allUsers/edit-eventOrganizer/:id",
    component: <EditEventOrganizer />,
    permission: "update",
  },
  {
    type: "collapse",
    name: "Manage Events",
    key: "manageEvents",
    icon: <Icon icon="material-symbols-light:folder-managed-outline" />,
    // permission: "events_management",
    collapse: [
      // {
      //   type: "collapse",
      //   name: "Add Event",
      //   key: "addEvent",
      //   icon: <Icon icon="icon-park-outline:dot" />,
      //   route: "/events/add-event",
      //   component: <AddEvent />,
      // },
      {
        type: "collapse",
        name: "Add Event",
        key: "addEventNew",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/events/add-eventNew",
        component: <AddEventWrapper />,
        permission: "create_events",
      },
      {
        type: "collapse",
        name: "View Events",
        key: "viewEvents",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/events/view-events",
        component: <Events />,
        permission: "view_events",
      },
      {
        type: "collapse",
        name: "Event Category",
        key: "eventCategory",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/events/event-category",
        component: <EventCategory />,
      },
    ]
  },
  {
    route: "/events/edit-event/:id",
    component: <AddEventWrapper />,
    permission: "edit_events",
  },
  {
    route: "/events/view-events/single-event/:id",
    component: <SingleEvent />,
    permission: "view_singleEvent",
  },
  {
    route: "/events/single-event/:id/view-form",
    component: <ViewForm />,
    permission: "view_forms",
  },
  {
    route: "/register/:eventId",
    component: <RegisterEvent />,
    permission: "register_events",
  },
  {
    route: "/events/single-event/:id/view-registrations",
    component: <ViewEventRegistrations />,
    permission: "view_registrations",
  },
  {
    route: "/events/add-mainEvent",
    component: <AddMainEvent />,
    permission: "create",
  },
  {
    route: "/events/edit-mainEvent/:id",
    component: <EditMainEvent />,
    permission: "update",
  },
  // {
  //   type: "collapse",
  //   name: "Theatres",
  //   key: "theatres",
  //   icon: <Icon icon="mdi:theater" />,
  //   route: "/theatres",
  //   component: <Theatres />,
  // },
  {
    type: "collapse",
    name: "Tickets",
    key: "manageTickets",
    icon: <Icon icon="pepicons-print:ticket-circle" />,
    permission: "manageTickets",
    collapse: [
      {
        type: "collapse",
        name: "Book Tickets",
        key: "eventBookings",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/eventBookings",
        component: <EventBookings />,
        permission: "book_tickets",
      },
      // {
      //   type: "collapse",
      //   name: "View Tickets",
      //   key: "viewTickets",
      //   icon: <Icon icon="icon-park-outline:dot" />,
      //   route: "/view-tickets",
      //   component: <ViewTickets />,
      // },
      {
        type: "collapse",
        name: "View Tickets",
        key: "viewEventTickets",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/view-eventTickets",
        component: <ViewTicketsByEvents />,
        permission: "view_tickets",
      },
    ]
  },
  {
    route: "/view-eventTickets/detailedView/:id",
    component: <DetailedTicketPageByEvents />,
    permission: "view_tickets",
  },
  {
    route: "/view-eventTickets/detailedView/singleRegistration/:id/:eventId",
    component: <DetailedTicketPageByRegistration />,
    permission: "view_tickets",
  },
  {
    route: "/view-eventTickets/detailedView/editRegistration/:id",
    component: <EditRegistration />,
    permission: "edit_registration",
  },
  {
    type: "title",
    name: "Venues Management",
    permission: "venues_title",
  },
  {
    type: "collapse",
    name: "Manage Venues",
    key: "venues",
    icon: <Icon icon="ph:building-office-thin" />,
    // permission: "manageVenues",
    collapse: [
      {
        type: "collapse",
        name: "Add Venue",
        key: "addVenues",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/add-venue",
        component: <AddVenue />,
        permission: "create_venue",
      },
      {
        type: "collapse",
        name: "All Venue",
        key: "venues",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/venues",
        component: <Venues />,
        permission: "view_venue",
      },
      {
        type: "collapse",
        name: "Venue Features",
        key: "venueFeature",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/venue-feature",
        component: <VenueFeature />,
      },
      {
        type: "collapse",
        name: "Add Venue Cities",
        key: "addVenueCities",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/add-venue-cities",
        component: <AddVenueCity />,
      },
      {
        type: "collapse",
        name: "Venue Cities",
        key: "venueCities",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/venue-cities",
        component: <VenueCities />,
      },
    ]
  },
  {
    route: "/venues/single-venue/:id",
    component: <SingleVenue />,
    permission: "view_singleVenue",
  },
  {
    route: "/venues/edit-venue/:id",
    component: <EditVenue />,
    permission: "edit_venue",
  },
  {
    route: "/venueCities/single-venueCity/:id",
    component: <SingleVenueCity />,
    permission: "view_singleVenueCity",
  },
  {
    route: "/venueCities/edit-venueCities/:id",
    component: <EditVenueCity />,
    permission: "edit_venueCity",
  },
  {
    type: "title",
    name: "User Management",
    permission: "users_title",
  },
  {
    type: "collapse",
    name: "Users",
    key: "users",
    icon: <Icon icon="ph:users-three-thin" />,
    route: "/allUsers",
    permission: "manageUsers",
    collapse: [
      // {
      //   type: "collapse",
      //   name: "Admin",
      //   key: "admin",
      //   icon: <Icon icon="icon-park-outline:dot" />,
      //   route: "allUsers/admin",
      //   component: <SuperAdmin />,
      //   permission: "read",
      // },
      // {
      //   type: "collapse",
      //   name: "Theatre Owners",
      //   key: "theatreOwners",
      //   icon: <Icon icon="icon-park-outline:dot" />,
      //   route: "/allUsers/theatreOwners",
      //   component: <TheatreOwners />,
      // },
      // {
      //   type: "collapse",
      //   name: "Event Organizers",
      //   key: "eventOrganizers",
      //   icon: <Icon icon="icon-park-outline:dot" />,
      //   route: "/allUsers/eventOrganizers",
      //   component: <EventOrganizers />,
      //   permission: "read",
      // },
      {
        type: "collapse",
        name: "Add User",
        key: "addUser",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/addUser",
        component: <AddUser />,
        permission: "create_users",
      },
      {
        type: "collapse",
        name: "All Users",
        key: "allUsers",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/allUsers",
        component: <AllUsers />,
        permission: "view_users",
      },
    ]
  },
  {
    type: "collapse",
    name: "Roles & Access",
    key: "roles",
    icon: <Icon icon="iconamoon:settings-thin" />,
    route: "/roles",
    permission: "manageRoles",
    collapse: [
      {
        type: "collapse",
        name: "Permission Types",
        key: "permissionTypes",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/permissionTypes",
        component: <ViewPermissionTypes />,
        // permission: "read",
      },
      {
        type: "collapse",
        name: "Permissions",
        key: "permissions",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/permissions",
        component: <ViewPermissions />,
        // permission: "read",
      },
      {
        type: "collapse",
        name: "Default Roles",
        key: "defaultRoles",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/defaultRoles",
        component: <ViewDefaultRoles />,
        permission: "view_userRoles",
      },
      {
        type: "collapse",
        name: "User Roles",
        key: "userRoles",
        icon: <Icon icon="icon-park-outline:dot" />,
        route: "/userRoles",
        component: <ViewUserRoles />,
        permission: "view_userRoles",
      },
    ],
  },
  {
    type: "title",
    name: "Others",
    permission: "default",
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon icon="iconamoon:settings-thin" />,
    route: "/settings",
    component: <Settings />,
    permission: "default",
  },
  {
    // type: "collapse",
    // name: "Profile",
    // key: "profile",
    // icon: <Icon icon="iconamoon:profile-circle-thin" />,
    route: "/profile",
    component: <Profile />,
    permission: "default",
  },
  // {
  //   type: "collapse",
  //   name: "Theatre",
  //   key: "theatre",
  //   icon: <Icon icon="mdi:theater" />,
  //   route: "/theatre",
  //   component: <SingleTheatre />,
  // },
  // {
  //   type: "collapse",
  //   name: "Movies",
  //   key: "movies",
  //   icon: <Icon icon="fluent:movies-and-tv-16-filled" />,
  //   route: "/movies",
  //   component: <Movies />,
  // },
  // {
  //   type: "collapse",
  //   name: "Movies Master Data",
  //   key: "movieMasterData",
  //   route: "/movieMasterData",
  //   icon: <Icon icon="ic:baseline-local-movies" />,
  //   collapse: [
  //     {
  //       type: "collapse",
  //       name: "Genre",
  //       key: "genre",
  //       icon: <Icon icon="mdi:film-star" />,
  //       route: "/genre",
  //       component: <Genre />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Censor Types",
  //       key: "movieMacensor-typesterData",
  //       icon: <Icon icon="ph:certificate-fill" />,
  //       route: "/censor-types",
  //       component: <CensorTypes />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Sound System",
  //       key: "soundsystem",
  //       icon: <Icon icon="icon-park-solid:sound" />,
  //       route: "/soundsystem",
  //       component: <SoundSystem />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Projection Type",
  //       key: "projection-type",
  //       icon: <Icon icon="mage:box-3d-scan-fill" />,
  //       route: "/projection-type",
  //       component: <ProjectionType />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Cast",
  //       key: "cast",
  //       icon: <Icon icon="fluent:people-star-16-filled" />,
  //       route: "/cast",
  //       component: <CastList />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Crew",
  //       key: "crew",
  //       icon: <Icon icon="fa6-solid:people-group" />,
  //       route: "/crew",
  //       component: <CrewList />,
  //     },
  //     // {
  //     //   type: "collapse",
  //     //   name: "Languages",
  //     //   key: "languages",
  //     //   icon: <Icon fontSize="small">translate</Icon>,
  //     //   route: "/languages",
  //     //   component: <Languages />,
  //     // },
  //   ],
  // },
  // {
  //   type: "collapse",
  //   name: "Theatre Master Data",
  //   key: "theatreMasterData",
  //   route: "/theatreMasterData",
  //   icon: <Icon icon="game-icons:theater" />,
  //   collapse: [
  //     {
  //       type: "collapse",
  //       name: "Sound System",
  //       key: "soundsystem",
  //       icon: <Icon icon="icon-park-solid:sound" />,
  //       route: "/soundsystem",
  //       component: <SoundSystem />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Facilities",
  //       key: "facilities",
  //       icon: <Icon icon="gis:search-feature" />,
  //       route: "/facilities",
  //       component: <Facilities />,
  //     },
  //     {
  //       type: "collapse",
  //       name: "Projection Type",
  //       key: "projection-type",
  //       icon: <Icon icon="mage:box-3d-scan-fill" />,
  //       route: "/projection-type",
  //       component: <ProjectionType />,
  //     },
  //   ],
  // },
  // {
  //   type: "collapse",
  //   name: "Shows",
  //   key: "shows",
  //   icon: <Icon icon="material-symbols:slideshow" />,
  //   route: "/shows",
  //   component: <AddShows />,
  // },
  {
    type: "collapse",
    name: "Bookings",
    key: "bookings",
    icon: <Icon icon="teenyicons:appointments-solid" />,
    route: "/bookings",
    component: <Bookings />,
    // permission:"read",
  },
  // {
  //   type: "collapse",
  //   name: "Bookings",
  //   key: "bookingsAdmin",
  //   icon: <Icon icon="teenyicons:appointments-solid" />,
  //   route: "/bookingsAdmin",
  //   component: <BookingsAdmin />,
  // },
  // {
  //   type: "collapse",
  //   name: "Book An Event",
  //   key: "eventBookings",
  //   icon: <Icon icon="emojione-monotone:admission-tickets" />,
  //   route: "/eventBookings",
  //   component: <EventBookings />,
  // },
  // {
  //   type: "collapse",
  //   name: "View Tickets",
  //   key: "viewTickets",
  //   icon: <Icon icon="pepicons-print:ticket-circle" />,
  //   route: "/view-tickets",
  //   component: <ViewTickets />,
  // },
  // {
  //   type: "collapse",
  //   name: "View Tickets",
  //   key: "viewEventTickets",
  //   icon: <Icon icon="pepicons-print:ticket-circle" />,
  //   route: "/view-eventTickets",
  //   component: <ViewEventTickets />,
  // },
  {
    type: "collapse",
    name: "Promotions",
    key: "promotions",
    icon: <Icon icon="hugeicons:promotion" />,
    route: "/promotions",
    component: <Promotions />,
    permission: "default",
  },
  {
    type: "collapse",
    name: "Feedbacks",
    key: "feedbacks",
    icon: <Icon icon="material-symbols-light:feedback-outline" />,
    route: "/feedbacks",
    component: <FeedBacks />,
    permission: "default",
  },
  {
    type: "collapse",
    name: "Help And Support",
    key: "helpAndSupport",
    icon: <Icon icon="material-symbols-light:help-outline-rounded" />,
    route: "/helpAndSupport",
    component: <HelpAndSupport />,
    permission: "default",
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon icon="material-symbols-light:partner-reports" />,
    route: "/reports",
    component: <Reports />,
    permission: "default",
  },
  {
    route: "/facilities/add-facilities",
    component: <AddFacilities />,
    permission: "create",
  },
  {
    route: "/movies/add-movie",
    component: <AddMovies />,
    permission: "create",
  },
  {
    route: "/languages/add-languages",
    component: <AddLanguages />,
    permission: "create",
  },
  {
    route: "/cast/add-cast",
    component: <AddCastList />,
    permission: "create",
  },
  {
    route: "/crew/add-crew",
    component: <AddCrewList />,
    permission: "create",
  },
  {
    route: "/genre/add-genre",
    component: <AddGenre />,
    permission: "create",
  },
  {
    route: "/censor-types/add-censor-types",
    component: <AddCensorType />,
    permission: "create",
  },
  {
    route: "/languages/edit-languages/:id",
    component: <EditLanguage />,
    permission: "update",
  },
  {
    route: "/cast/edit-cast/:id",
    component: <EditCastList />,
    permission: "update",
  },
  {
    route: "/movies/edit-movie/:id",
    component: <EditMovies />,
    permission: "update",
  },
  {
    route: "/movies/view-movie/:id",
    component: <ViewMovie />,
    permission: "read",
  },
  {
    route: "/crew/edit-crew/:id",
    component: <EditCrewList />,
    permission: "update",
  },
  {
    route: "/soundsystem/add-soundsystem",
    component: <AddSoundSystem />,
    permission: "create",
  },
  {
    route: "/projection-type/add-projection-type",
    component: <AddProjectionType />,
    permission: "create",
  },
  {
    route: "/tables/add-owner",
    component: <AddOwner />,
    permission: "create",
  },
  {
    route: "/tables/manage-owner",
    component: <ManageOwner />,
    permission: "update",
  },
  {
    route: "/theatres/add-theatre",
    component: <AddTheatre />,
    permission: "create",
  },
  {
    route: "/theatres/edit-theatre/:id",
    component: <EditTheatre />,
    permission: "update",
  },
  {
    route: "/facilities/edit-facilities/:id",
    component: <EditFacilities />,
    permission: "update",
  },
  {
    route: "/genre/edit-genre/:id",
    component: <EditGenre />,
    permission: "update",
  },
  {
    route: "/censor-types/edit-censor-types/:id",
    component: <EditCensorType />,
    permission: "update",
  },
  {
    route: "/soundsystem/edit-soundsystem/:id",
    component: <EditSoundSystem />,
    permission: "update",
  },
  {
    route: "/projection-type/edit-projection-type/:id",
    component: <EditProjectionType />,
    permission: "update",
  },
  {
    route: "/theatres/single-theatre/:id",
    component: <SingleTheatre />,
    permission: "read",
  },
  {
    route: "/eventOrganizations/single-eventOrganization/:id",
    component: <SingleEventOrganization />,
    permission: "read",
  },
  {
    route: "/eventOrganizations/edit-eventOrganization/:id",
    component: <EditEventOrganization />,
    permission: "update",
  },
  {
    route: "/theatres/single-theatre/add-screen/:id",
    component: <AddScreen />,
    permission: "create",
  },
  {
    route: "/theatres/single-theatre/single-screen/:screenId",
    component: <SingleScreen />,
    permission: "read",
  },
  {
    route: "/theatres/single-theatre/edit-screen/:screenId",
    component: <EditScreen />,
    permission: "update",
  },
  {
    route: "/theatres/single-theatre/single-screen/add-showTime/:screenId",
    component: <AddShowTime />,
    permission: "create",
  },
  {
    route: "/theatres/single-theatre/single-screen/edit-showTime/:showTimeId",
    component: <EditShowTime />,
    permission: "update",
  },
  {
    route: "/theatres/single-theatre/single-screen/add-zone/:screenId",
    component: <AddZone />,
    permission: "create",
  },
  {
    route: "/theatres/single-theatre/single-screen/single-zone/:id",
    component: <SingleZone />,
    permission: "read",
  },
  {
    route: "/theatres/single-theatre/single-screen/single-zone/edit-zone/:id",
    component: <EditZone />,
    permission: "update",
  },
  {
    route: "/venues/single-venue/add-zone/:venueId",
    component: <AddEventZone />,
    permission: "create",
  },
  {
    route: "/venues/single-venue/single-zone/:id",
    component: <SingleEventZone />,
    permission: "view_singleVenue",
  },
  {
    route: "/venues/single-venue/edit-zone/:id",
    component: <EditEventZone />,
    permission: "update",
  },
  {
    route: "/allUsers/add-theatreOwner",
    component: <AddTheatreOwner />,
    permission: "create",
  },
  {
    route: "/allUsers/edit-theatreOwner/:id",
    component: <EditTheatreOwner />,
    permission: "update",
  },
  {
    route: "/allUsers/theatreOwner/:id",
    component: <TheatreOwner />,
    permission: "read",
  },
  {
    route: "/bookings/book-seats/:showId/:screenId",
    component: <BookSeats />,
    permission: "read",
  },
  {
    route: "/eventBookings/book-seats/:eventId/:venueId",
    component: <SeatBookings />,
    permission: "read",
  },
  {
    route: "/bookings/book-seats/get-tickets",
    component: <GetTickets />,
    permission: "read",
  },
  {
    route: "/eventBookings/book-seats/get-tickets",
    component: <GetEventTicket />,
    permission: "read",
  },
  {
    route: "/eventBookings/book-ticket/:eventId",
    component: <TicketBookingAndRegistration />,
    permission: "book_tickets",
  },
  {
    route: "/eventBookings/book-ticket/ticket-view",
    component: <BookedTicketView />,
    permission: "book_tickets",
  },
  {
    route: "/viewTickets/single-ticket/:ticketId",
    component: <SingleTicketView />,
    permission: "view_tickets",
  },
  {
    route: "/permissions/add-permissions",
    component: <AddPermissions />,
    // permission: "create",
  },
  {
    route: "/permissions/edit-permissions/:id",
    component: <EditPermissions />,
    // permission: "update",
  },
  {
    route: "/defaultRoles/add-defaultRoles",
    component: <AddDefaultRoles />,
    // permission: "create",
  },
  {
    route: "/defaultRoles/edit-defaultRoles/:id",
    component: <EditDefaultRoles />,
    // permission: "update",
  },
  {
    route: "/defaultRoles/single-defaultRole/:id",
    component: <SingleDefaultRole />,
    // permission: "read",
  },
  {
    route: "/userRoles/add-userRoles",
    component: <AddUserRoles />,
    permission: "default",
  },
  {
    route: "/verifyEmail",
    component: <VerifyEmail />,
    permission: "default",
  },
  {
    route: "/verifyToken",
    component: <VerifyToken />,
    permission: "default",
  },
  {
    route: "/userRoles/invite-userRoles",
    component: <UserRoleInvite />,
    permission: "invite_userRole",
  },
  {
    route: "/userRoles/edit-userRoles/:id",
    component: <EditUserRoles />,
    // permission: "update",
  },
  {
    route: "/userRoles/single-userRole/:id",
    component: <SingleUserRole />,
    // permission: "read",
  },
  // {
  //   type: "collapse",
  //   name: "RTL",
  //   key: "rtl",
  //   icon: <Icon fontSize="small">format_textdirection_r_to_l</Icon>,
  //   route: "/rtl",
  //   component: <RTL />,
  // },
  // {
  //   type: "collapse",
  //   name: "Billing",
  //   key: "billing",
  //   icon: <Icon fontSize="small">receipt_long</Icon>,
  //   route: "/billing",
  //   component: <Billing />,
  // },
  // {
  //   type: "collapse",
  //   name: "Notifications",
  //   key: "notifications",
  //   icon: <Icon fontSize="small">notifications</Icon>,
  //   route: "/notifications",
  //   component: <Notifications />,
  // },
];

export default routes;